import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe2, Layers3, LineChart, Workflow } from 'lucide-react';
import SEO from '../components/SEO';
import { Section } from '../components/Section';
import { buildFAQSchema, GeoSeoSections } from '../components/GeoSeoSections';

const faqs = [
  {
    question: '¿Qué es un sistema multiagente para empresas en Brasil?',
    answer: 'Es una estructura donde varios agentes y automatizaciones colaboran para coordinar ventas, contenido, soporte, documentos y reporting desde una misma lógica.'
  },
  {
    question: '¿Por qué una empresa en Brasil usaría esta arquitectura?',
    answer: 'Porque permite crecer con más orden, menos dependencia de tareas manuales y mejor coordinación entre áreas comerciales y operativas.'
  },
  {
    question: '¿Puede adaptarse a expansión, partners o varios equipos?',
    answer: 'Sí. Precisamente el modelo multiagente encaja cuando hay más volumen, más canales y necesidad de visibilidad entre unidades o responsables.'
  },
  {
    question: '¿Neuralia369 implanta solo la idea o también la operación?',
    answer: 'La propuesta incluye aterrizaje operativo: criterios, flujos, prioridades y estructura para que el sistema sea usable, no solo una presentación.'
  }
];

const successCases = [
  {
    title: 'Empresa con ventas, soporte y marketing desconectados',
    copy: 'Se diseña una capa de coordinación para que tareas, información y prioridades no vivan separadas en herramientas aisladas.',
    outcome: 'más sincronía entre áreas y mejor ejecución'
  },
  {
    title: 'Operación en crecimiento con necesidad de escalar',
    copy: 'Arquitectura preparada para sumar automatizaciones, agentes y nuevas unidades sin romper el control central del negocio.',
    outcome: 'más escalabilidad sin perder claridad'
  },
  {
    title: 'Dirección que necesita ver rendimiento real',
    copy: 'Reporting y estructura de seguimiento visibles para decidir mejor sobre oportunidades, tareas críticas y cuellos de botella.',
    outcome: 'más visibilidad para liderazgo'
  }
];

const sectors = [
  { badge: 'Brasil', title: 'Operaciones comerciales complejas', copy: 'Encaja cuando hay muchas oportunidades, varios responsables y seguimiento continuo.' },
  { badge: 'Brasil', title: 'Agencias y partners', copy: 'Útil para entornos distribuidos con campañas, activos y reporting en múltiples frentes.' },
  { badge: 'Brasil', title: 'Servicios empresariales', copy: 'Excelente para coordinar propuestas, entregables, atención y dirección operativa.' },
  { badge: 'Brasil', title: 'Empresas en expansión', copy: 'Pensado para escalar con una base más robusta que hojas, chats y tareas dispersas.' }
];

const localSignals = [
  'El enfoque para Brasil prioriza sistemas multiagente con visión de crecimiento, coordinación y visibilidad ejecutiva.',
  'Es especialmente valioso en contextos con varios equipos, partners, unidades de negocio o alto volumen de acciones simultáneas.',
  'La propuesta une marketing, operación, contenido, reporting y seguimiento en una estructura más gobernable.',
  'No es solo automatización: es diseño de sistema para que la empresa pueda crecer con más consistencia.'
];

const faqSchema = buildFAQSchema(faqs);

export default function SistemaBrasilPage() {
  return (
    <>
      <SEO
        title="Sistema multiagente para empresas en Brasil | Neuralia369"
        description="Sistema multiagente para empresas en Brasil: marketing, operaciones, automatización y coordinación de procesos con Neuralia369."
        path="/sistema-multiagente-brasil"
        keywords="sistema multiagente Brasil, agentes IA Brasil, automatización empresarial Brasil, gestión con IA Brasil"
        schemas={[faqSchema]}
      />
      <div className="min-h-screen bg-background pt-24 pb-20" data-testid="sistema-brasil-page">
        <Section>
          <div className="max-w-6xl mx-auto grid gap-10 xl:grid-cols-[1.04fr_0.96fr]">
            <div>
              <p className="editorial-kicker mb-4">Brasil · Neuralia369</p>
              <h1>Sistema multiagente para empresas en <span className="gradient-text">Brasil</span></h1>
              <p className="mt-6 text-lg text-text-body">Una arquitectura empresarial para coordinar ventas, contenidos, operaciones y reporting desde una sola capa orquestadora preparada para equipos en crecimiento.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {['Brasil', 'Sistema multiagente', 'Gestión empresarial', 'IA aplicada'].map((item) => <span key={item} className="insight-chip">{item}</span>)}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contacto" className="btn-primary" data-testid="sistema-brasil-contact-cta">Solicitar información<ArrowRight className="w-5 h-5" /></Link>
                <Link to="/agenda" className="btn-secondary" data-testid="sistema-brasil-agenda-cta">Agendar sesión</Link>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                { icon: Layers3, title: 'Capa orquestadora', copy: 'Coordinación entre agentes y procesos con visión global.' },
                { icon: Workflow, title: 'Operación integrada', copy: 'Ventas, atención, documentos y acciones en una sola estructura.' },
                { icon: LineChart, title: 'Rendimiento', copy: 'Datos y prioridades visibles para el equipo.' },
                { icon: Globe2, title: 'Escalabilidad', copy: 'Pensado para expansión, partners y mercados distribuidos.' },
              ].map(({ icon: Icon, title, copy }) => (
                <div key={title} className="glass-card tracing-panel p-6" data-testid={`sistema-brasil-card-${title}`}>
                  <Icon className="w-6 h-6 text-primary" />
                  <p className="mt-4 text-lg font-semibold text-text-heading">{title}</p>
                  <p className="mt-3 text-sm text-text-body">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
        <GeoSeoSections
          slug="sistema-brasil"
          regionLabel="Brasil"
          successCases={successCases}
          sectors={sectors}
          faqs={faqs}
          localSignals={localSignals}
        />
      </div>
    </>
  );
}